import {Injectable} from '@angular/core';
import {
  GeneratedOfferRuleHandoffConsumptionResult,
  GeneratedOfferRuleHandoffFailureReason,
  generatedOfferRuleHandoffTtlMs,
  generatedOfferRuleHandoffVersion,
  generatedOfferRuleStorageNamespace,
  generatedOfferRuleTokenMaximumLength
} from '../model/generated-offer-rule-handoff.model';
import {OfferRuleModel} from '../model/offer-rule.model';

const tokenPattern = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const isoTimestampPattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/;
const maximumStringLength = 255;
const supportedConditions = ['New', 'Used', 'CPO', 'Any'];
const supportedStatuses = ['Active', 'Inactive'];
const allowedEnvelopeFields = ['version', 'token', 'accountId', 'createdAt', 'expiresAt', 'rule'];
const allowedRuleFields = [
  'accountId',
  'offerSetId',
  'year',
  'make',
  'model',
  'trim',
  'maxSalePrice',
  'vehicleLimit',
  'vehicleCondition',
  'dollarAmountDiscount',
  'isPrivate',
  'status'
];

@Injectable({
  providedIn: 'root'
})
export class GeneratedOfferRuleHandoffService {
  /**
   * Consumes a valid account-bound generated rule handoff token once.
   */
  consume(token: string, expectedAccountId: number): GeneratedOfferRuleHandoffConsumptionResult {
    let result = this.failure('malformed');
    if (this.isValidToken(token) && this.isPositiveInteger(expectedAccountId)) {
      result = this.consumeValidToken(token, expectedAccountId);
    }

    return result;
  }

  /**
   * Reads and consumes the stored envelope for a validated token.
   */
  private consumeValidToken(token: string, expectedAccountId: number): GeneratedOfferRuleHandoffConsumptionResult {
    let result = this.failure('storageUnavailable');
    try {
      const storage = window.localStorage;
      this.removeExpiredEntries(storage);
      const storageKey = `${generatedOfferRuleStorageNamespace}${token}`;
      const serializedEnvelope = storage.getItem(storageKey);
      result = this.consumeStoredEnvelope(storage, storageKey, serializedEnvelope, token, expectedAccountId);
    } catch (error: unknown) {
      result = this.failure('storageUnavailable');
    }

    return result;
  }

  /**
   * Validates a stored envelope and removes it after the consumption attempt.
   */
  private consumeStoredEnvelope(
    storage: Storage,
    storageKey: string,
    serializedEnvelope: string | null,
    token: string,
    expectedAccountId: number
  ): GeneratedOfferRuleHandoffConsumptionResult {
    let result = this.failure('missing');
    if (serializedEnvelope !== null) {
      result = this.parseAndValidate(serializedEnvelope, token, expectedAccountId);
      storage.removeItem(storageKey);
    }

    return result;
  }

  /**
   * Parses serialized handoff data and validates the resulting envelope.
   */
  private parseAndValidate(
    serializedEnvelope: string,
    token: string,
    expectedAccountId: number
  ): GeneratedOfferRuleHandoffConsumptionResult {
    let result = this.failure('malformed');
    try {
      const parsedEnvelope: unknown = JSON.parse(serializedEnvelope);
      result = this.validateEnvelope(parsedEnvelope, token, expectedAccountId);
    } catch (error: unknown) {
      result = this.failure('malformed');
    }

    return result;
  }

  /**
   * Validates envelope metadata and returns a sanitized generated rule.
   */
  private validateEnvelope(
    value: unknown,
    token: string,
    expectedAccountId: number
  ): GeneratedOfferRuleHandoffConsumptionResult {
    let result = this.failure('malformed');
    if (this.isRecord(value) && this.hasOnlyAllowedFields(value, allowedEnvelopeFields)) {
      const failureReason = this.getEnvelopeFailureReason(value, token, expectedAccountId);
      if (failureReason !== null) {
        result = this.failure(failureReason);
      }
      if (failureReason === null) {
        const sanitizedRule = this.sanitizeRule(value['rule'], expectedAccountId);
        if (sanitizedRule !== null) {
          result = {status: 'success', rule: sanitizedRule};
        }
      }
    }

    return result;
  }

  /**
   * Determines whether envelope identity, account, or timestamps are invalid.
   */
  private getEnvelopeFailureReason(
    envelope: Record<string, unknown>,
    token: string,
    expectedAccountId: number
  ): GeneratedOfferRuleHandoffFailureReason | null {
    let reason: GeneratedOfferRuleHandoffFailureReason | null = null;
    const accountId = envelope['accountId'];
    if (envelope['version'] !== generatedOfferRuleHandoffVersion || envelope['token'] !== token) {
      reason = 'malformed';
    }
    if (reason === null && (!this.isPositiveInteger(accountId) || accountId !== expectedAccountId)) {
      reason = 'accountMismatch';
    }
    if (reason === null) {
      reason = this.getTimestampFailureReason(envelope['createdAt'], envelope['expiresAt']);
    }

    return reason;
  }

  /**
   * Validates the handoff lifetime and reports expired timestamps separately.
   */
  private getTimestampFailureReason(
    createdAtValue: unknown,
    expiresAtValue: unknown
  ): GeneratedOfferRuleHandoffFailureReason | null {
    let reason: GeneratedOfferRuleHandoffFailureReason | null = 'malformed';
    if (typeof createdAtValue === 'string' && typeof expiresAtValue === 'string') {
      const createdAt = new Date(createdAtValue).getTime();
      const expiresAt = new Date(expiresAtValue).getTime();
      const now = Date.now();
      const lifetime = expiresAt - createdAt;
      const validTimestamps = this.isValidIsoTimestamp(createdAtValue) && this.isValidIsoTimestamp(expiresAtValue);
      if (validTimestamps && createdAt <= now && lifetime > 0 && lifetime <= generatedOfferRuleHandoffTtlMs) {
        reason = null;
        if (expiresAt <= now) {
          reason = 'expired';
        }
      }
    }

    return reason;
  }

  /**
   * Validates and sanitizes the generated rule for the expected account.
   */
  private sanitizeRule(value: unknown, accountId: number): OfferRuleModel | null {
    let rule: OfferRuleModel | null = null;
    if (this.isRecord(value) && this.hasOnlyAllowedFields(value, allowedRuleFields)) {
      const offerSetId = value['offerSetId'];
      const nestedAccountId = value['accountId'];
      const validAccount = this.isPositiveInteger(nestedAccountId) && nestedAccountId === accountId;
      if (this.isPositiveInteger(offerSetId) && validAccount && this.hasValidRuleFields(value)) {
        rule = this.buildSanitizedRule(value, accountId, offerSetId);
      }
    }

    return rule;
  }

  /**
   * Verifies all optional generated rule fields against their supported formats.
   */
  private hasValidRuleFields(rule: Record<string, unknown>): boolean {
    const validYear = this.isOptionalInteger(rule['year']);
    const validMake = this.isOptionalString(rule['make']);
    const validModel = this.isOptionalString(rule['model']);
    const validTrim = this.isOptionalString(rule['trim']);
    const validMaxSalePrice = this.isOptionalNonNegativeNumber(rule['maxSalePrice']);
    const validVehicleLimit = this.isOptionalNonNegativeNumber(rule['vehicleLimit']);
    const validDiscount = this.isOptionalPositiveNumber(rule['dollarAmountDiscount']);
    const validCondition = this.isOptionalAllowedString(rule['vehicleCondition'], supportedConditions);
    const validStatus = this.isOptionalAllowedString(rule['status'], supportedStatuses);
    const validPrivacy = typeof rule['isPrivate'] === 'boolean';

    return validYear && validMake && validModel && validTrim && validMaxSalePrice
      && validVehicleLimit && validDiscount && validCondition && validStatus && validPrivacy;
  }

  /**
   * Builds an Offer Rule from validated handoff values.
   */
  private buildSanitizedRule(
    value: Record<string, unknown>,
    accountId: number,
    offerSetId: number
  ): OfferRuleModel {
    const rule: OfferRuleModel = {
      accountId,
      offerSetId,
      year: this.optionalNumber(value['year']),
      make: this.optionalString(value['make']),
      model: this.optionalString(value['model']),
      trim: this.optionalString(value['trim']),
      maxSalePrice: this.optionalNumber(value['maxSalePrice']),
      vehicleLimit: this.optionalNumber(value['vehicleLimit']),
      dollarAmountDiscount: this.optionalNumber(value['dollarAmountDiscount']),
      vehicleCondition: this.optionalString(value['vehicleCondition']),
      status: this.optionalString(value['status']),
      isPrivate: value['isPrivate'] as boolean
    };

    return rule;
  }

  /**
   * Removes expired or malformed handoff entries from local storage.
   */
  private removeExpiredEntries(storage: Storage): void {
    const keysToRemove: string[] = [];
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (key && key.startsWith(generatedOfferRuleStorageNamespace)
        && this.isExpiredOrMalformed(storage.getItem(key), key)) {
        keysToRemove.push(key);
      }
    }
    for (const key of keysToRemove) {
      storage.removeItem(key);
    }
  }

  /**
   * Determines whether a stored handoff entry should be removed.
   */
  private isExpiredOrMalformed(serializedEnvelope: string | null, storageKey: string): boolean {
    let shouldRemove = true;
    if (serializedEnvelope !== null) {
      try {
        const value: unknown = JSON.parse(serializedEnvelope);
        if (this.isRecord(value) && this.hasOnlyAllowedFields(value, allowedEnvelopeFields)) {
          const token = storageKey.substring(generatedOfferRuleStorageNamespace.length);
          const accountId = value['accountId'];
          if (this.isPositiveInteger(accountId)) {
            const failureReason = this.getEnvelopeFailureReason(value, token, accountId);
            const sanitizedRule = this.sanitizeRule(value['rule'], accountId);
            shouldRemove = failureReason !== null || sanitizedRule === null;
          }
        }
      } catch (error: unknown) {
        shouldRemove = true;
      }
    }

    return shouldRemove;
  }

  /**
   * Verifies the token length and UUID format.
   */
  private isValidToken(token: string): boolean {
    const isValid = token.length <= generatedOfferRuleTokenMaximumLength && tokenPattern.test(token);

    return isValid;
  }

  /**
   * Rejects records containing fields outside the approved schema.
   */
  private hasOnlyAllowedFields(value: Record<string, unknown>, allowedFields: string[]): boolean {
    const fields = Object.keys(value);
    const hasOnlyAllowedFields = fields.every((field) => allowedFields.includes(field));

    return hasOnlyAllowedFields;
  }

  /**
   * Verifies a canonical UTC ISO timestamp.
   */
  private isValidIsoTimestamp(value: string): boolean {
    const timestamp = new Date(value);
    const isValid = isoTimestampPattern.test(value)
      && !Number.isNaN(timestamp.getTime())
      && timestamp.toISOString() === value;

    return isValid;
  }

  /**
   * Narrows unknown values to non-array records.
   */
  private isRecord(value: unknown): value is Record<string, unknown> {
    const isRecord = typeof value === 'object' && value !== null && !Array.isArray(value);

    return isRecord;
  }

  /**
   * Narrows unknown values to positive integers.
   */
  private isPositiveInteger(value: unknown): value is number {
    const isPositiveInteger = typeof value === 'number' && Number.isInteger(value) && value > 0;

    return isPositiveInteger;
  }

  /**
   * Accepts absent values or positive integers.
   */
  private isOptionalInteger(value: unknown): boolean {
    const isValid = value === undefined || value === null
      || (typeof value === 'number' && Number.isInteger(value) && value > 0);

    return isValid;
  }

  /**
   * Accepts absent values or bounded strings.
   */
  private isOptionalString(value: unknown): boolean {
    const isValid = value === undefined || value === null
      || (typeof value === 'string' && value.length <= maximumStringLength);

    return isValid;
  }

  /**
   * Accepts absent values or strings from an approved list.
   */
  private isOptionalAllowedString(value: unknown, allowedValues: string[]): boolean {
    const isValid = value === undefined || value === null
      || (typeof value === 'string' && allowedValues.includes(value));

    return isValid;
  }

  /**
   * Accepts absent values or finite non-negative numbers.
   */
  private isOptionalNonNegativeNumber(value: unknown): boolean {
    const isValid = value === undefined || value === null
      || (typeof value === 'number' && Number.isFinite(value) && value >= 0);

    return isValid;
  }

  /**
   * Accepts absent values or finite positive numbers.
   */
  private isOptionalPositiveNumber(value: unknown): boolean {
    const isValid = value === undefined || value === null
      || (typeof value === 'number' && Number.isFinite(value) && value > 0);

    return isValid;
  }

  /**
   * Trims string values while leaving other values absent.
   */
  private optionalString(value: unknown): string | undefined {
    let result: string | undefined;
    if (typeof value === 'string') {
      result = value.trim();
    }

    return result;
  }

  /**
   * Preserves numeric values while leaving other values absent.
   */
  private optionalNumber(value: unknown): number | undefined {
    let result: number | undefined;
    if (typeof value === 'number') {
      result = value;
    }

    return result;
  }

  /**
   * Creates a typed handoff failure result.
   */
  private failure(reason: GeneratedOfferRuleHandoffFailureReason): GeneratedOfferRuleHandoffConsumptionResult {
    const result: GeneratedOfferRuleHandoffConsumptionResult = {status: 'failure', reason};

    return result;
  }
}
